import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from './cn';
import Button from './Button';

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
  bodyClassName,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'ui-modal-title' : undefined}
        className={cn(
          'w-full rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary shadow-ga-elevated max-h-[90vh] flex flex-col',
          SIZES[size] || SIZES.md,
          className
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {(title || onClose) && (
          <div className="flex items-start justify-between gap-4 border-b border-ga-border-subtle px-6 py-4">
            <div>
              {title && (
                <h2 id="ui-modal-title" className="text-lg font-semibold text-ga-text-primary">
                  {title}
                </h2>
              )}
              {description && (
                <p className="mt-1 text-sm text-ga-text-muted">{description}</p>
              )}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-ga-sm p-1 text-ga-text-subtle hover:bg-ga-bg-surface-subtle hover:text-ga-text-primary"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        <div className={cn('overflow-y-auto px-6 py-4 flex-1', bodyClassName)}>{children}</div>
        {footer && (
          <div className="border-t border-ga-border-subtle px-6 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

export function ModalFooterActions({ children, className }) {
  return (
    <div className={cn('flex flex-col sm:flex-row gap-3', className)}>{children}</div>
  );
}

Modal.Footer = ModalFooterActions;
Modal.CloseButton = function ModalCloseButton(props) {
  return <Button variant="primary" className="w-full sm:w-auto" {...props} />;
};
